(() => {
  if (window.VidtoutProductCard) return;

  const loadedAlts = new WeakSet();

  function whenColorMediaReady(callback) {
    if (window.VidtoutColorMedia) {
      callback();
      return;
    }
    window.addEventListener('load', () => {
      if (window.VidtoutColorMedia) callback();
    }, { once: true });
  }

  function getCardCatalog(card) {
    if (!window.VidtoutColorMedia) return null;
    const dataEl = card.querySelector('.vidtout-color-image-data');
    if (!dataEl) return null;
    return VidtoutColorMedia.parseCatalog(dataEl.textContent);
  }

  function markAltReady(container, img) {
    if (!container || !img) return;
    if (img.complete) {
      container.classList.add('is-alt-ready');
    } else {
      img.addEventListener('load', () => container.classList.add('is-alt-ready'), { once: true });
    }
  }

  function applyHoverToAltContainer(altContainer, hover) {
    altContainer.dataset.altSrc = hover.cardUrl;
    altContainer.dataset.altSrcset = hover.cardSrcset || '';

    const existingImg = altContainer.querySelector('img');
    if (existingImg) {
      altContainer.classList.remove('is-alt-ready');
      existingImg.src = hover.cardUrl;
      if (hover.cardSrcset) {
        existingImg.srcset = hover.cardSrcset;
      } else {
        existingImg.removeAttribute('srcset');
      }
      markAltReady(altContainer, existingImg);
      loadedAlts.add(altContainer);
      return;
    }

    loadedAlts.delete(altContainer);
  }

  function applyCardColorImages(card, colorKey) {
    const catalog = getCardCatalog(card);
    if (!catalog || !colorKey || !window.VidtoutColorMedia) return null;

    const { primary, hover } = VidtoutColorMedia.getCardImages(catalog, colorKey);
    if (!primary) return null;

    const primaryImg = card.querySelector('.vidtout-product-card__image--primary img');
    if (primaryImg) {
      primaryImg.src = primary.cardUrl;
      if (primary.cardSrcset) {
        primaryImg.srcset = primary.cardSrcset;
      } else {
        primaryImg.removeAttribute('srcset');
      }
      if (primary.alt) primaryImg.alt = primary.alt;
    }

    const altContainer = card.querySelector('[data-vidtout-product-card-alt]');
    const altImg = card.querySelector('.vidtout-product-card__image--alt img');

    if (hover) {
      if (altContainer) {
        applyHoverToAltContainer(altContainer, hover);
      } else if (altImg) {
        altImg.src = hover.cardUrl;
        if (hover.cardSrcset) {
          altImg.srcset = hover.cardSrcset;
        } else {
          altImg.removeAttribute('srcset');
        }
        markAltReady(altImg.closest('.vidtout-product-card__image--alt'), altImg);
      }
    } else if (altContainer) {
      altContainer.removeAttribute('data-alt-src');
      altContainer.removeAttribute('data-alt-srcset');
      altContainer.classList.remove('is-alt-ready');
      const existingImg = altContainer.querySelector('img');
      if (existingImg) existingImg.remove();
      loadedAlts.delete(altContainer);
    }

    return { primary, hover };
  }

  function initInlineAltImages(root = document) {
    root.querySelectorAll('.vidtout-product-card__image--alt img').forEach((img) => {
      const wrap = img.closest('.vidtout-product-card__image--alt');
      if (!wrap) return;
      markAltReady(wrap, img);
    });
  }

  function loadAltImage(container) {
    if (!container || loadedAlts.has(container)) return;
    const src = container.dataset.altSrc;
    if (!src) return;

    const img = document.createElement('img');
    img.src = src;
    if (container.dataset.altSrcset) img.srcset = container.dataset.altSrcset;
    if (container.dataset.altSizes) img.sizes = container.dataset.altSizes;
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    markAltReady(container, img);
    container.appendChild(img);
    loadedAlts.add(container);
  }

  function preloadAlts(scope) {
    scope.querySelectorAll('[data-vidtout-product-card-alt]').forEach(loadAltImage);
  }

  function schedulePreload() {
    const run = () => {
      document.querySelectorAll('[data-preload-vidtout-product-card-alts]').forEach(preloadAlts);
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 1500 });
    } else {
      setTimeout(run, 1500);
    }
  }

  function applySwatchSelection(card, input) {
    if (!(input instanceof HTMLInputElement) || !input.checked) return;

    const colorKey = input.dataset.colorKey;
    const colorApplied = colorKey ? applyCardColorImages(card, colorKey) : null;

    const imageUrl = input.dataset.variantImage;
    const variantUrl = input.dataset.variantUrl;
    const variantPrice = input.dataset.variantPrice;
    const variantCompare = input.dataset.variantCompare;
    const onSale = input.dataset.variantOnSale === 'true';
    const isAvailable = input.dataset.optionAvailable !== 'false';

    if (!colorApplied?.primary) {
      const primary = card.querySelector('.vidtout-product-card__image--primary img');
      if (primary && imageUrl) {
        primary.src = imageUrl;
        const srcset = input.dataset.variantSrcset;
        if (srcset) {
          primary.srcset = srcset;
        } else {
          primary.removeAttribute('srcset');
        }
      }
    }

    if (variantUrl) {
      card.querySelectorAll('.vidtout-product-card__media-link, .vidtout-product-card__title').forEach((link) => {
        if (link instanceof HTMLAnchorElement) link.href = variantUrl;
      });
    }

    const variantInput = card.querySelector('[data-vidtout-card-variant-id]');
    const variantId = input.dataset.variantId;
    if (variantInput instanceof HTMLInputElement && variantId) {
      variantInput.value = variantId;
      variantInput.disabled = false;
    }

    const notifyBtn = card.querySelector('[data-vidtout-card-notify]');
    const cartForm = card.querySelector('[data-vidtout-card-cart]');
    const comingSoonBadge = card.querySelector('[data-vidtout-coming-soon-badge]');

    if (notifyBtn instanceof HTMLAnchorElement) {
      notifyBtn.hidden = isAvailable;
      const notifyBase = variantUrl || notifyBtn.href.split('#')[0];
      if (notifyBase) {
        notifyBtn.href = `${notifyBase.split('#')[0]}#restock-notify`;
      }
    }

    if (cartForm instanceof HTMLElement) {
      cartForm.hidden = !isAvailable;
    }

    if (comingSoonBadge instanceof HTMLElement) {
      comingSoonBadge.hidden = isAvailable;
    }

    const priceEl = card.querySelector('.vidtout-product-card__price-current');
    if (priceEl && variantPrice) {
      priceEl.textContent = variantPrice;
    }

    const compareEl = card.querySelector('.vidtout-product-card__compare');
    if (onSale && variantCompare) {
      if (compareEl) {
        compareEl.textContent = variantCompare;
      } else if (priceEl?.parentElement) {
        const compare = document.createElement('s');
        compare.className = 'vidtout-product-card__compare';
        compare.textContent = variantCompare;
        priceEl.parentElement.appendChild(compare);
      }
    } else if (compareEl) {
      compareEl.remove();
    }
  }

  function setSelectedSwatchLabel(fieldset, selectedLabel) {
    fieldset.querySelectorAll('.vidtout-product-card__swatch-label').forEach((label) => {
      label.classList.toggle('is-selected', label === selectedLabel);
    });
  }

  function initCardSwatches(card) {
    const fieldset = card.querySelector('.vidtout-product-card__swatches');
    if (!fieldset || fieldset.dataset.vidtoutSwatchesInit === 'true') return;
    fieldset.dataset.vidtoutSwatchesInit = 'true';

    fieldset.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      const selectedLabel = input.closest('.vidtout-product-card__swatch-label');
      if (selectedLabel) setSelectedSwatchLabel(fieldset, selectedLabel);
      applySwatchSelection(card, input);
    });

    fieldset.addEventListener('click', (event) => {
      event.stopPropagation();
    });
  }

  function initCards(root = document) {
    root.querySelectorAll('.vidtout-product-card').forEach((card) => {
      initCardSwatches(card);
      const checked = card.querySelector('.vidtout-product-card__swatch-input:checked');
      if (checked instanceof HTMLInputElement) {
        applySwatchSelection(card, checked);
      }
    });
  }

  if (window.matchMedia('(hover: hover)').matches) {
    document.addEventListener(
      'pointerenter',
      (e) => {
        const card = e.target.closest?.('.vidtout-product-card');
        if (!card || !e.target.closest?.('.vidtout-product-card__hover-zone')) return;
        const alt = card.querySelector('[data-vidtout-product-card-alt]');
        if (alt) loadAltImage(alt);

        const link = card.querySelector('.vidtout-product-card__media-link');
        if (link?.href && !navigator.connection?.saveData) {
          const prefetchId = 'prefetch-' + card.dataset.productId;
          if (!document.getElementById(prefetchId)) {
            const el = document.createElement('link');
            el.id = prefetchId;
            el.rel = 'prefetch';
            el.href = link.href;
            document.head.appendChild(el);
          }
        }
      },
      true
    );

    document.addEventListener(
      'focusin',
      (e) => {
        const card = e.target.closest?.('.vidtout-product-card');
        if (!card || !e.target.closest?.('.vidtout-product-card__hover-zone')) return;
        const alt = card.querySelector('[data-vidtout-product-card-alt]');
        if (alt) loadAltImage(alt);
      },
      true
    );

    window.addEventListener('load', schedulePreload, { once: true });
  }

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches?.('.vidtout-product-card') || node.querySelector?.('.vidtout-product-card')) {
          initInlineAltImages(node);
          whenColorMediaReady(() => initCards(node));
        }
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  whenColorMediaReady(() => {
    initInlineAltImages();
    initCards();
  });

  window.VidtoutProductCard = {
    preloadAlts,
    schedulePreload,
    initInlineAltImages,
    loadAltImage,
    initCards,
    applyCardColorImages,
  };
})();
